import React, { useState, useEffect } from 'react';
import { Button, Card, Row, Col, Select, Tag, Input, Divider, Typography, List, Space } from 'antd';
import { CheckCircleOutlined, SyncOutlined, MinusCircleOutlined } from '@ant-design/icons';
import { connect } from 'react-redux';
import { useKeycloak } from '@react-keycloak/web';
import { useLocation, useNavigate } from 'react-router-dom';
import MainLayout from '../../layouts/MainLayout';
import { addAppCustomNotification } from '../../components/NotificationBox';
import {
  executeQuery,
  executeQueryAdvanced,
  getKG_ServiceStatus,
  searchDataSet,
  searchDataSetBreakdownPerId,
  startKG_Service,
} from '../../services/kg_DataService';
import KgDetailedComponent from '../dashboard/KgDetailedComponent';

const { Paragraph } = Typography;
const { Search } = Input;

const SOURCE_OPTIONS = [
  { value: 'ADS', label: 'ADS' },
  { value: 'CDS', label: 'CDS' },
  { value: 'ADAM', label: 'ADAM' },
  { value: 'CMCC', label: 'CMCC' },
  { value: 'Eumetsat', label: 'Eumetsat' },
  { value: 'Noaa', label: 'Noaa' },
  { value: 'Sentinel', label: 'Sentinel' },
  { value: 'Silam', label: 'Silam' },
  { value: 'Inspire', label: 'Inspire' },
  { value: 'Istat', label: 'Istat' },
  { value: 'Land', label: 'Land' },
  { value: 'Marine', label: 'Marine' },
  { value: 'InSitu', label: 'InSitu' },
];

function mapDatasetResults(results) {
  const ds = [];
  for (let index = 0; index < results.length; index++) {
    const dataset = results[index].metadata;
    ds.push({
      id: results[index].id,
      persistentId: results[index].persistentID,
      title: dataset['Title'][0].values[0],
      description: dataset['Description'][0].values[0],
      isfromadam: dataset['Source'][0].values[0] === 'adam',
      isfromSentinel: dataset['Source'][0].values[0] === 'sentinel',
      fileformats:
        dataset['FileFormat'] !== undefined ? (
          <>
            {dataset['FileFormat'][0].values.map((item) => (
              <Tag color='cyan' key={item}>
                {item}
              </Tag>
            ))}
          </>
        ) : (
          <Tag color='red'>undefined</Tag>
        ),
      source: (
        <Tag color='blue' key={dataset['Source'][0].values[0]}>
          {dataset['Source'][0].values[0]}
        </Tag>
      ),
    });
  }
  return ds;
}

function DatasetSearchContent({ productsWF }) {
  const { keycloak } = useKeycloak();
  const location = useLocation();
  const navigate = useNavigate();

  const [serviceStatus, setServiceStatus] = useState('Stopped');
  const [searchResults, setSearchResults] = useState([]);
  const [serviceStarted, setServiceStarted] = useState(false);
  const [datasetInformations, setDatasetInformations] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [openDetailed, setOpenDetailed] = useState(false);
  const [configDetails, setApiConfigDetails] = useState({});
  const [selectedSearchedSources, setSelectedSearchedSources] = useState([]);
  const [selectedDataset, setSelectedDataset] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    getKG_ServiceStatus(keycloak.token).then((resp) => {
      setServiceStatus(resp.payload);
      if (resp.payload === 'Available') {
        setServiceStarted(true);
      }
    });
  }, [keycloak.token]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const q = params.get('q') || location.state?.query || '';
    if (q) {
      setSearchQuery(q);
      if (serviceStarted) {
        onSearchQuery(q);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search, location.state, serviceStarted]);

  const onServiceStart = () => {
    startKG_Service(keycloak.token).then((resp) => {
      if (resp === 'Done') {
        setServiceStarted(true);
        setServiceStatus('Available');
      }
    });
  };

  const handleSearchFailure = (query) => {
    setIsSearching(false);
    addAppCustomNotification(
      'Dataset Search',
      'CRITICAL',
      query ? `No results found for this query, '${query}'.` : 'No results found for this query, please try again later.',
    );
  };

  const processSearchResponse = (fake, query) => {
    if (!Array.isArray(fake) || fake.length === 0) {
      setSearchResults([]);
      setDatasetInformations([]);
      setIsSearching(false);
      handleSearchFailure(query);
      return;
    }

    setSearchResults(fake);
    const searchFn =
      selectedSearchedSources.length > 0
        ? () => searchDataSet(keycloak.token, fake)
        : () => searchDataSet(keycloak.token, fake, true);

    searchFn()
      .then((response) => {
        setDatasetInformations(mapDatasetResults(response.payload));
        setIsSearching(false);
      })
      .catch(() => {
        setIsSearching(false);
        addAppCustomNotification('Dataset Search', 'CRITICAL', 'No results found for this query, please try again later.');
      });
  };

  const onSearchQuery = (value) => {
    const query = value?.trim();
    if (!query) return;

    setSearchQuery(query);
    setSearchResults([]);
    setIsSearching(true);
    navigate('/workspace/dataset-search', { replace: true });

    const runQuery =
      selectedSearchedSources.length > 0
        ? executeQueryAdvanced(keycloak.token, query, selectedSearchedSources)
        : executeQuery(keycloak.token, query);

    runQuery
      .then((resp) => {
        if (resp !== '' && resp != null) {
          processSearchResponse(resp.payload, query);
        } else {
          handleSearchFailure(query);
        }
      })
      .catch(() => {
        setIsSearching(false);
        addAppCustomNotification('Dataset Search', 'CRITICAL', 'Encounter an error!');
      });
  };

  const CurrentTag = () => {
    if (serviceStatus === 'Available') {
      return (
        <Tag icon={<CheckCircleOutlined />} color='success'>
          {serviceStatus}
        </Tag>
      );
    }
    if (serviceStatus === 'Loading') {
      return (
        <Tag icon={<SyncOutlined spin />} color='processing'>
          {serviceStatus}
        </Tag>
      );
    }
    return (
      <Tag icon={<MinusCircleOutlined />} color='error'>
        {serviceStatus}
      </Tag>
    );
  };

  const SearchResultsSummary = () => {
    if (serviceStatus === 'Available' && searchResults.length > 0) {
      return (
        <Paragraph>
          <Divider />
          <p>
            We found a total of <b>{searchResults.length}</b> datasets
          </p>
        </Paragraph>
      );
    }
    return (
      <Paragraph>
        <Divider />
        <p>No Results were found! Please try another query</p>
      </Paragraph>
    );
  };

  const transformProductToCascaderOptions = (product) => {
    const { id, features } = product;
    return {
      value: id,
      label: `Product ${id}`,
      children: Object.keys(features).map((featureKey) => ({
        value: featureKey,
        label: featureKey.charAt(0).toUpperCase() + featureKey.slice(1),
        children: features[featureKey].map((featureValue) => ({
          value: featureValue,
          label: featureValue,
        })),
      })),
    };
  };

  const getAndSetBreakdownDataset = (id) => {
    searchDataSetBreakdownPerId(keycloak.token, id)
      .then((res) => {
        if (JSON.stringify(res) !== '{}' && JSON.stringify(res) !== JSON.stringify({ Error: 'Invalid input' })) {
          const result = JSON.parse(JSON.stringify(res));
          const products = result.payload.products;
          const newOptions = [];

          if (result.payload.metadata.DownloadOption !== undefined) {
            result.payload.metadata.DownloadOption[0].values.forEach((element) => {
              newOptions.push({ label: element, value: element });
            });
          }

          const productsTree = products.map((element) => transformProductToCascaderOptions(element));
          const productDict = products.reduce((acc, product) => {
            acc[product.id] = product.features;
            return acc;
          }, {});

          setApiConfigDetails({
            json: productDict,
            cascader: productsTree,
            download_options: newOptions,
          });
        } else {
          setApiConfigDetails({ json: {}, cascader: [], download_options: [] });
        }
      })
      .catch(() => {
        addAppCustomNotification('Dataset Search', 'CRITICAL', 'Encounter an error!');
        setApiConfigDetails({ json: {}, cascader: [], download_options: [] });
      });
  };

  const onOpenDetailedDataset = (datasetId, item) => {
    const dataSetInfo = datasetInformations.find((d) => d.id === datasetId);
    if (dataSetInfo && !dataSetInfo.isfromadam && !dataSetInfo.isfromSentinel) {
      getAndSetBreakdownDataset(datasetId);
    }
    setSelectedDataset(item);
    setOpenDetailed(true);
  };

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <CurrentTag />
        <Button type='primary' disabled={serviceStarted} onClick={onServiceStart}>
          Start KG Service
        </Button>
      </div>

      {serviceStarted && (
        <Card title='Query Search'>
          <Row>
            <Col span={24}>
              <Space.Compact style={{ width: '100%' }} size='large'>
                <Select
                  placeholder='Select Sources (Leave it blank means all options selected)'
                  showSearch
                  style={{ width: '30%' }}
                  optionFilterProp='children'
                  filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
                  onChange={setSelectedSearchedSources}
                  multiple
                  maxTagCount='responsive'
                  mode='tags'
                  size='large'
                  options={SOURCE_OPTIONS}
                />
                <Search
                  placeholder="Enter a string to make a research e.g: 'climate change'"
                  allowClear
                  enterButton='Search'
                  style={{ width: '100%' }}
                  size='large'
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onSearch={onSearchQuery}
                />
              </Space.Compact>
            </Col>
          </Row>

          <SearchResultsSummary />
          <List
            itemLayout='vertical'
            loading={isSearching}
            size='large'
            pagination={{ pageSize: 10 }}
            dataSource={datasetInformations}
            renderItem={(item) => (
              <List.Item key={item.id}>
                <List.Item.Meta
                  title={
                    <Button type='link' style={{ padding: 0, height: 'auto' }} onClick={() => onOpenDetailedDataset(item.id, item)}>
                      {item.title}
                    </Button>
                  }
                  description={item.description}
                />
                {item.source} |
                <span>
                  <i style={{ marginLeft: 15 }}>From Adam: </i>
                  {item.isfromadam ? (
                    <Tag color='green'>YES</Tag>
                  ) : (
                    <Tag color='red'>NO</Tag>
                  )}
                </span>
              </List.Item>
            )}
          />
        </Card>
      )}

      {selectedDataset && (
        <KgDetailedComponent
          datasetid={selectedDataset.id}
          persistentId={selectedDataset.persistentId}
          dataset={selectedDataset}
          breakdown={configDetails}
          isopen={openDetailed}
          productsWF={productsWF}
          sources={selectedSearchedSources}
          close={() => {
            setOpenDetailed(false);
            setSelectedDataset(null);
          }}
        />
      )}
    </>
  );
}

const mapStateToProps = (state) => ({
  productsWF: state.products.items,
});

const ConnectedDatasetSearchContent = connect(mapStateToProps)(DatasetSearchContent);

function DatasetSearchView() {
  return (
    <MainLayout>
      <ConnectedDatasetSearchContent />
    </MainLayout>
  );
}

export { ConnectedDatasetSearchContent as DatasetSearchContent };
export default DatasetSearchView;
